// Potidaea mockup — Phase 1 wire.
// Adds WizardConnect connection + live chipnet balance to the hand-authored HTML
// mockup at app.html. The existing inline JS (asset picker, wizard, review sheet,
// AMM rendering) is untouched; we hook in through:
//   1. A global window.potidaeaWc handle exposing the WC session snapshot
//   2. Direct DOM mutation of the wallet chip + slot markers this file inserts
//   3. Global window.ASSETS mutation to overwrite bal on `paired` — the inline
//      JS's recalc()/renderKat() already picks up mutated ASSETS on next input.
//
// Nothing broadcasts in Phase 1. The Swap "Confirm" / Artemision "Broadcast" /
// Publish "Publish" buttons still open their mock flows; a small banner is
// added to each so the visitor understands the visual outcome is illustrative
// and Phase 2 will wire the actual signing round-trip via WizardConnect.

import './wc.css';
import { ElectrumClient, fetchAddressUtxos, summariseBalance } from './electrum';
import { WcClient } from './wc';
import { claimViaWc, claimViaLocalSigner, claimViaLocalSignerShare, redeemViaLocalSigner, KNOWN_MBAS, mergeKnownMbas, previewClaim } from './orbital';
import { genesisArtemisionMBA, genesisDeviantShareMBA, loadLocalKnownMbas, type GenesisResult, type ShareGenesisResult, type ShareGenesisParams } from './genesis';
import { publishViaLocalSigner, PublishError, TEMPLATE_ARTEMISION_MINT_FT, TEMPLATE_DEVIANT_SHARE_MBA, type PublishMetadata } from './publish';
import { uploadIcon, IconUploadError } from './icon-upload';
import { bchUsd, refreshBchUsd } from './pricing';
import { BCH_ICON_DATA_URI } from './icons';
import { readKatalogos, fetchEntryParams, discoveredParamsToKnownMba, fetchAndVerifyIcon, type RegisteredEntry } from './registry';
import { decodeCashAddress } from '@bitauth/libauth';
import {
  loadActiveSigner, importUserWif, createUserWallet, clearUserWallet, readUserWif,
  listUserWallets, switchUserWallet, removeUserWallet, renameUserWallet,
  pinDevWif, clearDevWif, type Signer,
} from './signer-store';

// ── known chipnet CashToken categories the mockup asks the chain about ──────
// Ticker → hex category id. Only tickers present in the mockup's ASSETS array
// and holding a real chipnet category can have their bal replaced with live
// numbers; unknown ones keep whatever was hard-coded (or stay 0 for demo).
// Kept in sync with shells/app/src/core/wallet.ts CHIPNET_DEMO_TOKENS.
const KNOWN_CATEGORIES: Readonly<Record<string, string>> = {
  ASM:  '09af8a5b2fc2c9a2449045a8d5a6aa60db493989682eca257a57fa8d75d67231',
  ASM2: '8a9f592d4a3bdbc1bc23e57c151434cbebb79bc3a00a07e7f457c9ae8fea2815',
  CAP:  '155cdb786c2a34d79db937659d76e74459d9535b2a8c59661dcf5a6e82bb4a9d',
};

// ── mockup <-> Vite glue ────────────────────────────────────────────────────
// The inline JS in app.html declares window.ASSETS + window.recalc + window.renderKat
// implicitly (as `var` at script-top scope). We read + mutate them here through
// the window handle; TypeScript's `unknown` cast avoids leaking a fat type surface.

interface MockAsset { sym: string; name: string; glyph: string; rate: number; cls: string; bal: number; pub?: boolean; iconUrl?: string; }
interface WindowExt {
  ASSETS?: MockAsset[];
  recalc?: () => void;
  renderKat?: () => void;
  potidaeaWc?: PotidaeaWcHandle;
  // Mockup swap-card state (declared via `var` in inline JS — visible on window)
  from?: string;
  to?: string;
}
declare global { interface Window extends WindowExt {} }

/** Public handle the inline JS (or a future Phase 2 signing wire) can call. */
export interface PotidaeaWcHandle {
  connect(): void;
  disconnect(): void;
  refreshBalance(): Promise<void>;
  readonly cashaddr: string | null;
}

const wc = new WcClient({
  network: 'chipnet',
  dappName: 'Potidaea',
  dappIcon: undefined,   // TODO: 128×128 favicon-alike data-URL
});

// ── UI hookup — the wallet chip in the topbar ───────────────────────────────
// The existing HTML holds a fake chip: <div class="wallet-chip"> with a dot,
// short address, and BCH figure. We replace its contents on every WC state
// change. The class stays so the surrounding CSS keeps working.
function chipEl(): HTMLElement | null { return document.querySelector('.wallet-chip'); }

function renderChip(): void {
  const el = chipEl();
  if (!el) return;
  paintWalletMenu();
  // Local signer (user or dev) takes precedence: paint chip as attached.
  if (localSigner) { renderChipLocal(el, localSigner); return; }
  const snap = wc.snapshot;
  el.classList.toggle('wc-connected', snap.phase === 'paired');
  el.classList.toggle('wc-idle', snap.phase === 'idle');
  el.classList.toggle('wc-connecting', snap.phase === 'awaiting-scan');
  el.classList.toggle('wc-error', snap.phase === 'error');
  if (snap.phase === 'paired' && snap.cashaddr) {
    const addr = snap.cashaddr;
    const short = addr.slice(0, 14) + '…' + addr.slice(-6);
    el.innerHTML =
      '<span class="wc-dot"></span>' +
      '<span class="wc-addr" title="' + addr + '">' + short + '</span>' +
      ' · <b id="chipBch">…</b>' +
      ' <span class="chev">▾</span>';
  } else if (snap.phase === 'awaiting-scan') {
    el.innerHTML = '<span class="wc-dot"></span><span class="wc-addr">connecting…</span> <span class="chev">▾</span>';
  } else {
    // Idle — keep the chip a single click target; the click opens #walletMenu
    // via the mockup's own coordinated toggle (netMenu ↔ walletMenu mutex,
    // outside-click closes). Do NOT inject a nested <button> here — that
    // would swallow the outer chip's click handler.
    el.innerHTML = '<span class="wc-dot"></span><span class="wc-addr">no wallet attached</span> <span class="chev">▾</span>';
  }
}

function renderChipLocal(el: HTMLElement, s: Signer): void {
  el.classList.remove('wc-idle', 'wc-connecting', 'wc-error');
  el.classList.add('wc-connected');
  const addr = s.cashaddr;
  const short = addr.slice(0, 14) + '…' + addr.slice(-6);
  const badgeTxt = s.mode === 'dev' ? 'dev' : 'local';
  const badgeTitle = s.mode === 'dev'
    ? 'dev signer · localhost only · pinned via console'
    : 'key held by this page (not a paired wallet)';
  el.innerHTML =
    '<span class="wc-dot"></span>' +
    '<span class="wc-addr" title="' + addr + ' (' + badgeTitle + ')">' + short + '</span>' +
    ' · <b id="chipBch">…</b>' +
    ' <span class="wc-mode-badge" title="' + badgeTitle + '">' + badgeTxt + '</span>' +
    ' <span class="chev">▾</span>';
}

function detachLocalSigner(): void {
  if (!localSigner) return;
  if (localSigner.mode === 'user') {
    // Chip ✕ = remove just this one. Other stored wallets stay put and one
    // of them will take over automatically. Same UX as clicking ✕ on the
    // active row of the wallet menu.
    removeStoredWallet(localSigner.cashaddr);
    return;
  }
  // dev mode: single-slot, no siblings — clear the pin
  clearDevWif();
  localSigner = null;
  renderChip();
  paintWalletMenu();
  zeroAllBalances();
}

// ── QR/URI overlay — shown while awaiting-scan ──────────────────────────────
function scrimEl(): HTMLElement | null { return document.getElementById('wc-scrim'); }
function ensureScrim(): HTMLElement {
  let el = scrimEl();
  if (el) return el;
  el = document.createElement('div'); el.id = 'wc-scrim';
  el.className = 'wc-scrim';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Connect a WizardConnect wallet</div>' +
      '<p class="wc-modal-sub">Open your wallet, tap Connect, scan or paste this URI.</p>' +
      '<div class="wc-uri-box"><code id="wc-uri">…</code></div>' +
      '<div class="wc-actions">' +
        '<button class="wc-copy">Copy URI</button>' +
        '<button class="wc-cancel">Cancel</button>' +
      '</div>' +
      '<div class="wc-hint">First-time users: install a WizardConnect-compatible wallet (Deviant wallet, or any BCH wallet with WizardConnect). Nothing on this page ever holds your keys.</div>' +
    '</div>';
  document.body.appendChild(el);
  el.addEventListener('click', (ev) => { if (ev.target === el) wc.disconnect(); });
  el.querySelector('.wc-cancel')?.addEventListener('click', () => wc.disconnect());
  el.querySelector('.wc-copy')?.addEventListener('click', async () => {
    const uri = wc.snapshot.uri; if (!uri) return;
    try { await navigator.clipboard.writeText(uri); } catch { /* clipboard blocked; user selects manually */ }
    const btn = el!.querySelector('.wc-copy') as HTMLButtonElement | null; if (btn) { btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy URI'; }, 1500); }
  });
  return el;
}

function renderScrim(): void {
  const snap = wc.snapshot;
  if (snap.phase !== 'awaiting-scan' || !snap.uri) {
    scrimEl()?.remove();
    return;
  }
  const el = ensureScrim();
  const code = el.querySelector('#wc-uri') as HTMLElement | null;
  if (code) code.textContent = snap.uri;
}

// ── Balance refresh — on `paired`, hit chipnet electrum ──────────────────────
let currentClient: ElectrumClient | null = null;

async function refreshBalance(): Promise<void> {
  // Dev-signer takes precedence: when localhost + WIF pinned, refresh against
  // its cashaddr regardless of WC state. Fall back to WC snapshot otherwise.
  const cashaddr = localSigner?.cashaddr ?? (wc.snapshot.phase === 'paired' ? wc.snapshot.cashaddr : null);
  if (!cashaddr) return;
  if (!currentClient) {
    try { currentClient = await ElectrumClient.connectAny(); }
    catch { paintBalanceErr('offline'); return; }
  }
  try {
    const utxos = await fetchAddressUtxos(currentClient, cashaddr);
    console.log('[potidaea] balance for', cashaddr, '→', utxos.length, 'utxos');
    const bal = summariseBalance(utxos);
    paintBalance(bal.bchSats, bal.ftByCategory);
  } catch (e) {
    paintBalanceErr(e instanceof Error ? e.message : 'error');
  }
}

function paintBalance(bchSats: bigint, ftByCategory: Map<string, bigint>): void {
  const bchEl = document.getElementById('chipBch');
  const bch = Number(bchSats) / 1e8;
  if (bchEl) bchEl.textContent = bch.toFixed(4) + ' BCH';

  const assets = window.ASSETS;
  if (!assets) return;

  // Whitelist of tickers whose balance we can source from chipnet in this
  // build. Anything outside this set is a mockup demo asset with no on-chain
  // category yet — its stored `bal` is a hard-coded number from the HTML
  // that would mislead the user (their real wallet holds 0). Zero them out
  // so the Balances page + swap card reflect the actual wallet.
  const RESOLVED = new Set<string>(['BCH', ...Object.keys(KNOWN_CATEGORIES)]);

  const bchRow = assets.find((a) => a.sym === 'BCH');
  if (bchRow) bchRow.bal = bch;

  for (const [sym, cat] of Object.entries(KNOWN_CATEGORIES)) {
    const asset = assets.find((a) => a.sym === sym);
    if (!asset) continue;
    const raw = ftByCategory.get(cat) ?? 0n;
    // FTs in the mockup ASSETS are already denominated in whole units; the
    // covenants in this project store the same. If future assets have
    // decimals, add a per-ticker divisor here.
    asset.bal = Number(raw);
  }
  for (const asset of assets) {
    if (!RESOLVED.has(asset.sym)) asset.bal = 0;
  }

  // Re-run every derived display the mockup exposes on window. The order
  // matters only weakly (all read from ASSETS), but doing them all ensures
  // the Balances page, swap card, and Katalogos row totals all move in
  // lockstep with the wallet.
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { paintOdAll(); } catch { /* noop */ }
}

function paintBalanceErr(reason: string): void {
  const bchEl = document.getElementById('chipBch');
  if (bchEl) bchEl.textContent = reason;
}

// ── Phase 1 banner on Swap / Genesis / Publish ──────────────────────────────
// Add a small always-visible note inside the review sheet + genesis panel +
// publish overlay so users know why "Confirm" doesn't move on-chain funds yet.
function injectPhase1Banner(): void {
  const style = document.createElement('style');
  style.textContent = `
    .wc-p1-banner { font-family: var(--font-mono); font-size: 10px; letter-spacing: .06em; color: #ffb56b; background: rgba(255,150,80,.06); border: 1px solid rgba(255,150,80,.22); border-radius: 8px; padding: 8px 10px; margin: 10px 0; line-height: 1.6; }
    .wc-p1-banner b { color: #ffd0a0; }
  `;
  document.head.appendChild(style);
  const noteHtml = '<div class="wc-p1-banner"><b>Phase 2 wire</b> · <b>BCH → ASM/ASM2</b> and <b>ASM/ASM2 → BCH</b> broadcast real chipnet transactions (claim + redeem-and-burn). MBA → MBA composite swap, genesis, and publish are still preview until later phases wire them.</div>';

  // Inject once into the review sheet
  const rev = document.getElementById('reviewSheet');
  if (rev && !rev.querySelector('.wc-p1-banner')) rev.insertAdjacentHTML('afterbegin', noteHtml);
  // …and into the genesis panel
  const gen = document.getElementById('genesisState');
  if (gen && !gen.querySelector('.wc-p1-banner')) gen.insertAdjacentHTML('afterbegin', noteHtml);
  // Publish overlay is dynamically created — patch openPublishOverlay via a MutationObserver
  new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes as unknown as HTMLElement[]) {
        if (node instanceof HTMLElement && node.classList.contains('publish-scrim')) {
          const modal = node.querySelector('.publish-modal');
          if (modal && !modal.querySelector('.wc-p1-banner')) modal.insertAdjacentHTML('afterbegin', noteHtml);
        }
      }
    }
  }).observe(document.body, { childList: true });
}

// ── Boot ────────────────────────────────────────────────────────────────────
wc.on('change', (snap) => {
  renderChip();
  renderScrim();
  if (snap.phase === 'paired') void refreshBalance();
  if (snap.phase === 'idle') {
    currentClient?.close(); currentClient = null;
    // If nothing else is holding balances (no local signer), the WC
    // wallet just disconnected — wipe the UI so we don't show stale
    // numbers from a wallet that isn't attached anymore.
    if (!localSigner) zeroAllBalances();
  }
});

// ── Phase 2a swap-confirm interception — BCH → known MBA claim via WC ──────
// Attaches to #sheetConfirm with capture:true so it runs BEFORE the mockup's
// inline confirmSwap handler. When conditions apply (WC paired + from=BCH +
// to∈KNOWN_MBAS) we take over — stopImmediatePropagation prevents the mock
// from also running; on any other combination the mock still fires.
// dev-signer state — loaded synchronously at boot on localhost when the
// localStorage WIF is set. When present, takes precedence over WC — the
// swap-confirm handler builds + sends the tx directly, no WC round-trip.
let localSigner: Signer | null = null;
type SwapPlan =
  | { ok: true; kind: 'claim'; ticker: string; via: 'wc' | 'dev' }
  | { ok: true; kind: 'redeem'; ticker: string; via: 'dev' }
  | { ok: false };

function templateOf(ticker: string): number {
  const m = KNOWN_MBAS[ticker];
  return (m?.templateId ?? 0x00) & 0xff;
}

/** Templates whose CLAIM path orbital.ts implements. Redeem is separate —
 *  see isRedeemable — because Share MBAs redeem the same way as Artemision
 *  (plain P2PKH burn; the covenant only constrains the claim direction). */
function isClaimable(ticker: string): boolean {
  const t = templateOf(ticker);
  return t === 0x00 || t === 0x01;
}
function isRedeemable(ticker: string): boolean {
  // Both Artemision and Share MBAs self-back each FT UTXO; a plain P2PKH
  // spend that burns the FT amount releases the embedded backing without
  // going through any covenant. Redeem works for both templates.
  const t = templateOf(ticker);
  return t === 0x00 || t === 0x01;
}

function isEligibleRealSwap(): SwapPlan {
  const from = window.from, to = window.to;
  // BCH → MBA: covenant claim() path (WC or local signer)
  if (from === 'BCH' && to && to in KNOWN_MBAS && isClaimable(to)) {
    // WC path only knows the Artemision covenant; Share MBA WC is a follow-up.
    if (localSigner) return { ok: true, kind: 'claim', ticker: to, via: 'dev' };
    if (wc.snapshot.phase === 'paired' && templateOf(to) === 0x00) {
      return { ok: true, kind: 'claim', ticker: to, via: 'wc' };
    }
  }
  // MBA → BCH: plain P2PKH burn + backing-release. Local signer only for now.
  if (to === 'BCH' && from && from in KNOWN_MBAS && isRedeemable(from) && localSigner) {
    return { ok: true, kind: 'redeem', ticker: from, via: 'dev' };
  }
  return { ok: false };
}

async function handleRealSwapConfirm(): Promise<void> {
  const chk = isEligibleRealSwap();
  if (!chk.ok) return;   // mock flow takes over via the default handler
  const amtEl = document.getElementById('amtIn') as HTMLInputElement | null;
  const amtRaw = Number(amtEl?.value);
  const m = KNOWN_MBAS[chk.ticker];

  // Compute amountUnits from the FROM input.
  //   claim (BCH → MBA): amtIn is whole BCH → sats → sats / rate = units
  //   redeem (MBA → BCH): amtIn is whole MBA units → floor to bigint
  let amountUnits: bigint;
  if (chk.kind === 'claim') {
    const grossSats = BigInt(Math.floor(amtRaw * 1e8));
    amountUnits = grossSats / m.rateSats;
    if (amountUnits < 1n) {
      setSheetError(`Amount too small — buy at least ${Number(m.rateSats) / 1e8} BCH worth (1 ${chk.ticker} unit)`);
      return;
    }
  } else {
    amountUnits = BigInt(Math.floor(amtRaw));
    if (amountUnits < 1n) { setSheetError(`Amount too small — redeem at least 1 ${chk.ticker}`); return; }
  }

  // Show the pending state on the review sheet (the mockup's own UI transition)
  const pendingSheet = document.getElementById('pendingSheet');
  const reviewSheet = document.getElementById('reviewSheet');
  const doneSheet = document.getElementById('doneSheet');
  if (reviewSheet) (reviewSheet as HTMLElement).style.display = 'none';
  if (pendingSheet) (pendingSheet as HTMLElement).style.display = '';
  if (doneSheet) (doneSheet as HTMLElement).style.display = 'none';

  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    let result: { txid: string; ticker: string; amountUnits: bigint };
    let doneLabel: string;
    if (chk.kind === 'claim') {
      // Template dispatch — Share MBA's claim has a different tx shape
      // (fee split into two outputs) than Artemision, and its covenant is a
      // different artifact. templateOf(ticker) picks the right variant.
      const claimFn = templateOf(chk.ticker) === 0x01 ? claimViaLocalSignerShare : claimViaLocalSigner;
      const r = chk.via === 'dev'
        ? await claimFn(localSigner!, { ticker: chk.ticker, amountUnits })
        : await claimViaWc(wc, currentClient, {
            ticker: chk.ticker,
            amountUnits,
            payerCashaddr: wc.snapshot.cashaddr!,
          });
      result = r;
      doneLabel = String(r.amountUnits) + ' ' + r.ticker;
    } else {
      const r = await redeemViaLocalSigner(localSigner!, { ticker: chk.ticker, amountUnits });
      result = { txid: r.txid, ticker: r.ticker, amountUnits: r.amountUnits };
      const bch = (Number(r.releasedSats) / 1e8).toFixed(8);
      doneLabel = bch + ' BCH ← burned ' + String(r.amountUnits) + ' ' + r.ticker;
    }
    // Flip done sheet; write the real txid + amount
    if (pendingSheet) (pendingSheet as HTMLElement).style.display = 'none';
    if (doneSheet) (doneSheet as HTMLElement).style.display = '';
    const doneTxid = document.getElementById('doneTxid');
    if (doneTxid) doneTxid.textContent = result.txid;
    const doneAmt = document.getElementById('doneAmt');
    if (doneAmt) doneAmt.textContent = doneLabel;
    // Log the swap for the tx-history column.
    logTx({ kind: chk.kind === 'claim' ? 'claim' : 'redeem', ticker: result.ticker, amount: doneLabel, txid: result.txid });
    // Refresh balances so the chip + swap card reflect the trade
    void refreshBalance();
  } catch (e) {
    setSheetError(e instanceof Error ? e.message : String(e));
    if (pendingSheet) (pendingSheet as HTMLElement).style.display = 'none';
    if (reviewSheet) (reviewSheet as HTMLElement).style.display = '';
  }
}

function setSheetError(msg: string): void {
  let el = document.getElementById('wc-sheet-err') as HTMLElement | null;
  if (!el) {
    el = document.createElement('div'); el.id = 'wc-sheet-err';
    el.style.cssText = 'font-family:var(--font-mono);font-size:11px;color:#ff7a8a;background:rgba(255,122,138,.08);border:1px solid rgba(255,122,138,.28);border-radius:8px;padding:8px 10px;margin:10px 0;';
    const rev = document.getElementById('reviewSheet');
    rev?.insertBefore(el, rev.firstChild);
  }
  el.textContent = '✗ ' + msg;
}

function wireSwapConfirmInterceptor(): void {
  const btn = document.getElementById('sheetConfirm');
  if (!btn) return;
  btn.addEventListener('click', (ev) => {
    const chk = isEligibleRealSwap();
    if (!chk.ok) return;   // let mockup's confirmSwap run
    ev.stopImmediatePropagation();
    ev.preventDefault();
    void handleRealSwapConfirm();
  }, { capture: true });
}

async function bootLocalSigner(): Promise<void> {
  localSigner = loadActiveSigner();
  if (!localSigner) return;
  console.log('[signer]', localSigner.mode, '· cashaddr', localSigner.cashaddr);
  renderChip();
  void refreshBalance();
}

function boot(): void {
  attachWellKnownIcons();
  hydrateLocalMbas();
  filterAssetsToSwappable();
  renderChip();
  paintWalletMenu();
  injectPhase1Banner();
  wireSwapConfirmInterceptor();
  wireArtemisionBroadcast();
  injectTemplatePicker();
  wireIconAutoUpload();
  wirePublishInterceptor();
  wireOdLayout();
  installAmountPlaceholder();
  installBalancesUsdPatch();
  // Balances page + swap card still show the mockup's hard-coded numbers
  // until a wallet attaches. Zero them out on boot so the user sees "you
  // hold nothing" honestly — the moment a wallet attaches, paintBalance
  // fills in the real chipnet numbers. If a signer is already loaded from
  // localStorage, bootLocalSigner immediately fetches + paints; the wipe
  // is instantaneous and invisible.
  zeroAllBalances();
  void bootLocalSigner();
  void refreshBchUsd().then(() => updateAmountPlaceholder());
  void refreshRegistry();
}

// ── Katalogos registry read — surface who is registered on-chain ───────────
// Deliberately best-effort + view-only for v1. See registry.ts for why the
// swap picker cannot include unknown-metadata entries yet (v1 stores the
// category id but not the rate/fee/founder needed to build a claim tx).
// What we can do today: show a "registered on chipnet" chip in the Katalogos
// tab so users see the on-chain list grow past our static seed.

let registryEntries: RegisteredEntry[] = [];

async function refreshRegistry(): Promise<void> {
  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    const entries = await readKatalogos(currentClient);
    registryEntries = entries;
    console.log('[katalogos]', entries.length, 'entries on chipnet:', entries.map((e) => e.categoryId.slice(0, 8)));
    (window as unknown as { potidaeaRegistry: RegisteredEntry[] }).potidaeaRegistry = entries;
    paintRegistryBanner();

    // Cross-user metadata read: for each entry, try the DVMB OP_RETURN
    // sidecar. Successes fold into KNOWN_MBAS + ASSETS so wallets that
    // have NEVER seen this asset before can now swap it. Fetches are
    // parallel with generous timeouts — a per-entry failure is logged
    // and skipped.
    let mergedCount = 0;
    const results = await Promise.allSettled(entries.map((e) => fetchEntryParams(currentClient!, e).then((p) => ({ e, p }))));
    for (const r of results) {
      if (r.status !== 'fulfilled' || !r.value.p) continue;
      try {
        const km = await discoveredParamsToKnownMba(r.value.e, r.value.p);
        if (!km) continue;
        // Prefer static seed / local cache over discovered — those already
        // ran the full genesis + local metadata contract.
        if (!KNOWN_MBAS[km.ticker] || KNOWN_MBAS[km.ticker].categoryId === km.categoryId) {
          mergeKnownMbas({ [km.ticker]: km });
          const assets = window.ASSETS;
          if (assets && !assets.find((a) => a.sym === km.ticker)) {
            const rawName = r.value.p.name ?? km.ticker;
            // Only append the provenance suffix once — earlier fallback
            // sometimes already embedded "from Katalogos" into the ticker
            // shorthand.
            const displayName = /from Katalogos/i.test(rawName) ? rawName : rawName + ' · from Katalogos';
            assets.push({
              sym: km.ticker,
              name: displayName,
              glyph: km.ticker.slice(0, 2),
              rate: Number(km.rateSats),
              cls: 'MBA',
              bal: 0,
              pub: true,
            });
          }
          // Icon sidecar — fire-and-forget verify+render. On success the
          // ASSETS row's iconUrl gets set and the next recalc/renderKat
          // paints the <img>. On any failure we silently keep the glyph
          // fallback (no leak, no scary UI — a bad icon means "this
          // publisher's Sia path is broken", not "the asset is bad").
          const icon = r.value.p;
          if (icon.iconSha256Hex && icon.iconPath) {
            void fetchAndVerifyIcon(icon.iconPath, icon.iconSha256Hex).then((dataUrl) => {
              if (!dataUrl) return;
              const row = window.ASSETS?.find((a) => a.sym === km.ticker) as
                | { iconUrl?: string } | undefined;
              if (row) row.iconUrl = dataUrl;
              try { window.recalc?.(); } catch { /* noop */ }
              try { window.renderKat?.(); } catch { /* noop */ }
              try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
            });
          }
          mergedCount++;
        }
      } catch (err) {
        console.warn('[katalogos] discover fail for', r.value.e.categoryId.slice(0, 12), err instanceof Error ? err.message : err);
      }
    }
    if (mergedCount) {
      console.log('[katalogos] discovered params for', mergedCount, 'entries via DVMB sidecars');
      try { window.recalc?.(); } catch { /* noop */ }
      try { window.renderKat?.(); } catch { /* noop */ }
    }
    // Registry read done → always repaint the assets browser (both success
    // and no-new-merges paths reach here) so the "Listed" filter reflects
    // the freshest count.
    try { paintOdAll(); } catch { /* noop */ }
  } catch (e) {
    console.warn('[katalogos] refresh failed:', e instanceof Error ? e.message : e);
  }
}

function paintRegistryBanner(): void {
  // Attach a small pill on the Katalogos tab's Stoa panel that says how many
  // entries live on-chain vs how many we can transact with. Non-intrusive:
  // if the target element doesn't exist (mockup swap), we no-op.
  const target = document.getElementById('kt-stoa') || document.querySelector('.view#v-katalogos');
  if (!target) return;
  let banner = document.getElementById('wc-reg-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'wc-reg-banner';
    banner.style.cssText = 'font-family:var(--font-mono);font-size:11px;letter-spacing:.04em;color:var(--acid);background:rgba(157,240,90,.06);border:1px solid rgba(157,240,90,.22);border-radius:8px;padding:8px 12px;margin:10px 0;line-height:1.55;';
    target.insertBefore(banner, target.firstChild);
  }
  const knownSyms = Object.keys((window as unknown as { potidaeaKnown?: unknown }).potidaeaKnown ?? {}); // hook for future use
  void knownSyms;
  const n = registryEntries.length;
  if (n === 0) {
    banner.textContent = '⌁ Katalogos on chipnet · empty (no on-chain listings yet)';
  } else {
    const shorts = registryEntries.slice(0, 3).map((e) => e.categoryId.slice(0, 8) + '…').join(' · ');
    const more = n > 3 ? ` · +${n - 3} more` : '';
    banner.innerHTML = '⌁ Katalogos on chipnet · <b>' + n + ' asset' + (n === 1 ? '' : 's') + '</b> registered · <span style="color:var(--muted)">' + shorts + more + '</span><br>' +
      '<span style="color:var(--muted);font-size:10px">v1 registry stores the category id only; rate/fee/founder come from a metadata seed. New listings are visible here but not yet swappable in Potidaea until a v2 covenant carries the parameters (ADR-0055 Amendment 2026-09-02).</span>';
  }
}

// Filter the mockup's ASSETS array down to what actually swaps on chipnet:
//   • BCH always
//   • Every ticker in KNOWN_MBAS (seed + local cache + DVMB-discovered)
// The mockup's hardcoded demo tickers (GMX, CHE, ATD, …) have no covenant
// wired, so keeping them in the picker was misleading — user picks GMX and
// the "swap" is inert. Now they're removed from the picker (and by
// extension from every derived surface: balances page, Katalogos rows,
// review sheet). Real GMX / CHE covenants can be added later by seeding
// KNOWN_MBAS with their categoryId + params.
function filterAssetsToSwappable(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  const KEEP = new Set<string>(['BCH', ...Object.keys(KNOWN_MBAS)]);
  const removed = assets.filter((a) => !KEEP.has(a.sym)).map((a) => a.sym);
  window.ASSETS = assets.filter((a) => KEEP.has(a.sym));
  if (removed.length) console.log('[assets] filtered non-swappable:', removed);
  // Rewire from/to so the mockup's picker never lands on a removed sym.
  // Default to BCH → first non-BCH swappable, matching the "buy an asset
  // with BCH" mental model most users start with.
  const nonBch = window.ASSETS.find((a) => a.sym !== 'BCH');
  window.from = 'BCH';
  window.to = nonBch?.sym ?? 'BCH';
  try { window.recalc?.(); } catch { /* noop */ }
}

// Merge locally-genesised MBAs into KNOWN_MBAS + ensure they surface as
// swap-picker rows on the mockup's ASSETS array. This runs at boot so a
// user who created an asset in a prior session sees it immediately.
function hydrateLocalMbas(): void {
  const local = loadLocalKnownMbas();
  const localCount = Object.keys(local).length;
  if (localCount === 0) return;
  mergeKnownMbas(local);
  const assets = window.ASSETS;
  if (!assets) return;
  for (const [ticker, m] of Object.entries(local)) {
    if (assets.find((a) => a.sym === ticker)) continue;
    assets.push({
      sym: ticker,
      name: m.name ?? ticker + ' · your asset',
      glyph: m.glyph ?? ticker.slice(0, 2),
      rate: Number(m.rateSats),
      cls: 'MBA',
      bal: 0,
      pub: false,
      iconUrl: m.iconUrl,
    });
  }
  console.log('[hydrate] merged', localCount, 'local MBAs into KNOWN_MBAS');
}

// Wizard "Broadcast genesis" button — replaces the mockup's illustrative
// pseudo-txid handler with a real ArtemisionMintFT deploy on chipnet.
// Requires an attached local signer (WC-side genesis is a follow-up — same
// tx shape, needs the inputPaths triplet + a wallet that supports it).
function wireArtemisionBroadcast(): void {
  const btn = document.getElementById('wizBroadcast');
  if (!btn) return;
  btn.addEventListener('click', (ev) => {
    if (!localSigner) return;         // no signer → mock's illustrative flow takes over
    ev.stopImmediatePropagation();
    ev.preventDefault();
    void runArtemisionGenesis();
  }, { capture: true });
}

// Template picker — injected at the top of wizard step 3. Two options today:
//   • Static (Artemision)  → genesisArtemisionMBA (existing default)
//   • Shares + dividends   → genesisDeviantShareMBA (fee split at consensus)
// Selecting Shares reveals a small panel with the three extra fields the
// Share covenant needs: companySplitBps + company address + distribution
// address. Both addresses default to the signer's own bchtest: address so a
// solo dev can smoke the flow without staging separate keys.
function injectTemplatePicker(): void {
  const step3 = document.querySelector('.wiz-body[data-body="3"]');
  if (!step3 || document.getElementById('wc-tpl-picker')) return;

  // Segmented control at the top
  const picker = document.createElement('div');
  picker.id = 'wc-tpl-picker';
  picker.className = 'fld';
  picker.innerHTML =
    '<label>Template <span style="opacity:.6; text-transform:none; letter-spacing:0">(family of covenant · picked once at genesis)</span></label>' +
    '<div class="wc-tpl-seg">' +
      '<button type="button" class="wc-tpl-opt on" data-tpl="artemision">' +
        '<b>Static</b><span>Artemision · fixed backing rate + founder fee</span>' +
      '</button>' +
      '<button type="button" class="wc-tpl-opt" data-tpl="share">' +
        '<b>Shares + dividends</b><span>DeviantShareMBA · fee splits into company + distribution streams</span>' +
      '</button>' +
    '</div>';
  step3.insertBefore(picker, step3.firstChild?.nextSibling ?? null);

  // Share-only panel (hidden by default). Placed just after the fee row.
  const feeRow = document.getElementById('asFee')?.closest('.fld-row');
  if (feeRow) {
    const sharePanel = document.createElement('div');
    sharePanel.id = 'wc-share-panel';
    sharePanel.style.cssText = 'display:none; margin-top:14px; padding:14px; border:1px solid rgba(214,255,61,.22); background:rgba(214,255,61,.04); border-radius:10px;';
    sharePanel.innerHTML =
      '<div style="font-family:var(--font-mono); font-size:10px; letter-spacing:.16em; text-transform:uppercase; color:var(--acid); margin-bottom:10px">Shares + dividends</div>' +
      '<div class="fld-row">' +
        '<div class="fld"><label>Company share of fee (bps · max 10000)</label><input id="asCompanySplit" value="5000" inputmode="numeric"></div>' +
        '<div class="fld"><label style="opacity:.7">Shareholder stream</label><div style="font-family:var(--font-mono); font-size:12px; color:var(--muted); padding:10px 0">= fee − company (auto)</div></div>' +
      '</div>' +
      '<div class="fld"><label>Company address <span style="opacity:.6; text-transform:none; letter-spacing:0">(bchtest: — defaults to your wallet)</span></label><input id="asCompanyAddr" placeholder="bchtest:qz…" autocomplete="off" spellcheck="false"></div>' +
      '<div class="fld"><label>Distribution address <span style="opacity:.6; text-transform:none; letter-spacing:0">(bchtest: — where shareholders\' stream lands · defaults to your wallet)</span></label><input id="asDistribAddr" placeholder="bchtest:qz…" autocomplete="off" spellcheck="false"></div>';
    feeRow.parentNode?.insertBefore(sharePanel, feeRow.nextSibling);
  }

  // Small local stylesheet for the segmented control
  const style = document.createElement('style');
  style.textContent =
    '.wc-tpl-seg { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }' +
    '.wc-tpl-opt { background: var(--surface-2); border: 1px solid var(--border-strong); color: var(--ink-bright); padding: 12px 14px; border-radius: 10px; cursor: pointer; text-align: left; font-family: var(--font-display); transition: border-color .12s, background .12s; }' +
    '.wc-tpl-opt b { display: block; font-size: 14px; font-weight: 500; }' +
    '.wc-tpl-opt span { display: block; font-family: var(--font-mono); font-size: 10px; color: var(--muted); margin-top: 4px; line-height: 1.5; }' +
    '.wc-tpl-opt:hover { border-color: rgba(214, 255, 61, .35); }' +
    '.wc-tpl-opt.on { border-color: var(--acid); background: rgba(214, 255, 61, .06); }' +
    '.wc-tpl-opt.on b { color: var(--acid); }';
  document.head.appendChild(style);

  // Click handling
  picker.querySelectorAll('.wc-tpl-opt').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      picker.querySelectorAll('.wc-tpl-opt').forEach((b) => b.classList.remove('on'));
      (btn as HTMLElement).classList.add('on');
      const tpl = btn.getAttribute('data-tpl') || 'artemision';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).wizTemplate = tpl;
      const panel = document.getElementById('wc-share-panel');
      if (panel) panel.style.display = (tpl === 'share') ? '' : 'none';
    });
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).wizTemplate = 'artemision';
}

function readTemplate(): 'artemision' | 'share' {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = (window as any).wizTemplate;
  return t === 'share' ? 'share' : 'artemision';
}

async function runArtemisionGenesis(): Promise<void> {
  if (!localSigner) return;
  const ticker = (document.getElementById('asSym') as HTMLInputElement | null)?.value?.trim() || 'NEW';
  const name   = (document.getElementById('asName') as HTMLInputElement | null)?.value?.trim() || undefined;
  const glyph  = (document.getElementById('asGlyph') as HTMLInputElement | null)?.value?.trim() || undefined;
  const rateStr = (document.getElementById('asRate') as HTMLInputElement | null)?.value?.trim() || '100';
  const feePct = parseFloat((document.getElementById('asFee') as HTMLInputElement | null)?.value ?? '0');
  const rateSats = BigInt(Math.max(1, Math.floor(Number(rateStr))));
  const feeBps = BigInt(Math.max(0, Math.min(500, Math.round(feePct * 100))));
  const tpl = readTemplate();

  const state = document.getElementById('genesisState');
  if (state) state.innerHTML =
    '<div class="review-row"><span class="rk">Broadcasting</span><span class="rv acid">' + escHtml(ticker) + ' · ' + (tpl === 'share' ? 'Shares' : 'Static') + ' · rate ' + String(rateSats) + ' sats/unit · fee ' + String(feeBps) + ' bps</span></div>' +
    '<div style="font-family:var(--font-mono); font-size:11px; color: var(--muted); margin-top: 12px">Waiting for chipnet confirmation…</div>';

  try {
    let res: GenesisResult | ShareGenesisResult;
    if (tpl === 'share') {
      const splitStr = (document.getElementById('asCompanySplit') as HTMLInputElement | null)?.value?.trim() || '5000';
      const companySplitBps = BigInt(Math.max(0, Math.min(10000, Math.floor(Number(splitStr)))));
      const companyAddrRaw = (document.getElementById('asCompanyAddr') as HTMLInputElement | null)?.value?.trim() || '';
      const distribAddrRaw = (document.getElementById('asDistribAddr') as HTMLInputElement | null)?.value?.trim() || '';
      const companyLBHex = companyAddrRaw ? cashaddrToP2PKHLBHex(companyAddrRaw) : pkhToP2PKHLBHex(localSigner.publicKeyHash);
      const distribLBHex = distribAddrRaw ? cashaddrToP2PKHLBHex(distribAddrRaw) : pkhToP2PKHLBHex(localSigner.publicKeyHash);
      if (!companyLBHex) throw new Error('Invalid company address — expected bchtest: cashaddr');
      if (!distribLBHex) throw new Error('Invalid distribution address — expected bchtest: cashaddr');
      const shareRes: ShareGenesisResult = await genesisDeviantShareMBA(localSigner, {
        ticker, rateSats, feeBps, companySplitBps,
        companyLockingBytecodeHex: companyLBHex,
        distributionLockingBytecodeHex: distribLBHex,
        name, glyph,
      });
      res = shareRes;
    } else {
      res = await genesisArtemisionMBA(localSigner, { ticker, rateSats, founderFeeBps: feeBps, name, glyph });
    }
    logTx({ kind: 'genesis', ticker, amount: (tpl === 'share' ? 'Shares' : 'Static'), txid: res.txid });
    // Now visible in KNOWN_MBAS (via saveLocalAsset inside genesis) — but we
    // also want to re-hydrate ASSETS in place so the picker refreshes.
    hydrateLocalMbas();

    // Icon path: prefer the URL the file-change auto-upload already
    // produced (see wireIconAutoUpload). If that didn't fire — user
    // pasted a WIF, opened a wizard from cache, or auto-upload failed —
    // fall back to uploading from the wizard's data-URL preview now.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const row = window.ASSETS?.find((a) => a.sym === ticker) as
      | { iconSha256?: string; iconPath?: string; iconUrl?: string } | undefined;
    if (typeof w.wizIconUploadedUrl === 'string' && typeof w.wizIconUploadedSha === 'string') {
      if (row) { row.iconSha256 = w.wizIconUploadedSha; row.iconPath = w.wizIconUploadedUrl; }
    } else if (typeof w.wizIconDataUrl === 'string' && w.wizIconDataUrl.startsWith('data:')) {
      try {
        const { bytes, contentType } = dataUrlToBytes(w.wizIconDataUrl);
        const upload = await uploadIcon(bytes, contentType);
        if (row) { row.iconSha256 = upload.sha256Hex; row.iconPath = upload.url; }
        console.log('[icon] uploaded at genesis-time', upload.bytes, 'bytes →', upload.url);
      } catch (e) {
        const msg = e instanceof IconUploadError ? '[' + e.code + '] ' + e.message : String(e);
        console.warn('[icon] upload skipped:', msg);
      }
    }
    try { window.recalc?.(); } catch { /* noop */ }
    try { window.renderKat?.(); } catch { /* noop */ }
    if (state) {
      state.innerHTML =
        '<div class="done-mark"><div class="dm">✓</div>' +
          '<h4>' + escHtml(ticker) + ' minted on chipnet</h4>' +
          '<p>Genesis broadcast — <b>added to your swap picker</b> automatically. Ready to trade.</p>' +
          '<div class="txid">' + escHtml(res.txid) + '</div>' +
          '<div class="txid" style="font-size:10px; opacity:.7">category ' + escHtml(res.categoryId) + '</div>' +
          '<div style="margin-top:22px; padding:16px; border-radius:12px; border:1px solid rgba(214,255,61,.3); background:rgba(214,255,61,.05); text-align:left">' +
            '<div style="font-family:var(--font-mono); font-size:10px; letter-spacing:.16em; text-transform:uppercase; color:var(--acid); margin-bottom:8px">Optional · publish to the shared Katalogos</div>' +
            '<p style="margin:0 0 12px; font-size:13px; line-height:1.7; color:var(--ink-soft)">Inscribes ' + escHtml(ticker) + ' into the on-chain registry every wallet reads. Costs <b>$1</b> in DEA (oracle-priced at broadcast).</p>' +
            '<button class="wiz-btn primary" style="width:100%" onclick="openPublishOverlay(\'' + escAttr(ticker) + '\')">Publish for $1 in DEA →</button>' +
          '</div>' +
        '</div>';
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (state) state.innerHTML =
      '<div style="font-family:var(--font-mono); font-size:12px; color: #ff7a8a; background: rgba(255,122,138,.08); border: 1px solid rgba(255,122,138,.28); border-radius: 8px; padding: 12px">' +
      '✗ Genesis failed<br><span style="font-size:11px; opacity:.8">' + escHtml(msg) + '</span></div>' +
      '<button class="wiz-btn primary" style="width:100%; margin-top:16px" onclick="location.reload()">Reload</button>';
  }
}

// Publish button — replaces the mockup's illustrative openPublishOverlay
// with a real KatalogosRegistryV1.register() broadcast when a local signer
// is attached. Structured as a monkey-patch on window.openPublishOverlay so
// EVERY entry point (post-genesis "Publish for $1 in DEA" button, Katalogos
// tab's Publish links, Stoa row buttons) routes through the real path.
// Falls back to the mockup's illustrative flow when no signer is present.
function wirePublishInterceptor(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const originalOverlay = w.openPublishOverlay;
  if (typeof originalOverlay !== 'function' || w.__potidaeaPublishWrapped) return;
  w.openPublishOverlay = function wrappedPublish(sym: string) {
    if (!localSigner) return originalOverlay.apply(this, arguments as unknown as unknown[]);
    void runRealPublish(sym);
  };
  w.__potidaeaPublishWrapped = true;
}

async function runRealPublish(sym: string): Promise<void> {
  if (!localSigner) return;
  const mba = KNOWN_MBAS[sym];
  if (!mba) { alert('Cannot publish ' + sym + ': no local metadata for this ticker'); return; }

  // Show a lightweight scrim so the user sees what's happening (the mockup's
  // rich publish overlay has ~500 lines of preflight UI — we shortcut to a
  // single-modal broadcast until we port that surface. Errors surface here.)
  const scrim = document.createElement('div');
  scrim.className = 'wc-scrim'; scrim.id = 'wc-publish-scrim';
  scrim.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Publish ' + escHtml(sym) + ' to Katalogos</div>' +
      '<p class="wc-modal-sub">Inscribes the category on-chain in the shared registry (chipnet v1). Fee: <b>~$1 in DEA</b> (231 atomic units at chipnet oracle rate).</p>' +
      '<div id="wc-pub-body"><div style="font-family:var(--font-mono);font-size:11px;color:var(--muted)">Building + broadcasting…</div></div>' +
      '<div class="wc-actions" style="margin-top:14px">' +
        '<button class="wc-cancel" id="wc-pub-close">Close</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(scrim);
  const body = scrim.querySelector('#wc-pub-body') as HTMLElement;
  const closeBtn = scrim.querySelector('#wc-pub-close') as HTMLButtonElement;
  closeBtn.addEventListener('click', () => scrim.remove());

  try {
    // Pull the display metadata off the mockup's ASSETS row for this
    // ticker (Artemision-created assets stash their glyph/name/icon
    // there via app.html wizBroadcast + genesis.ts). Fall back to bare
    // ticker if the row is gone.
    const assetRow = window.ASSETS?.find((a) => a.sym === sym) as
      | { name?: string; iconSha256?: string; iconPath?: string }
      | undefined;
    const displayName = assetRow?.name?.split(' · ')[0];
    // Only inscribe icon chunks when both sha + a fetchable URL are
    // present — the mockup can transiently hold an s3://bns/… fake path
    // (before upload completes), and publishing THAT would put an
    // unfetchable string on-chain forever.
    const iconPath = assetRow?.iconPath;
    const iconOk = !!(assetRow?.iconSha256 && iconPath && /^https?:\/\//.test(iconPath));
    // Template-aware payload: Share MBA (0x01) publishes the company/
    // distribution + split; Artemision (0x00 / undefined) publishes the
    // founder LB. Common fields (ticker/name/icon) ride on top per DVMB.
    const meta: PublishMetadata = mba.templateId === 0x01 ? {
      templateId: TEMPLATE_DEVIANT_SHARE_MBA,
      rateSats: mba.rateSats,
      feeBps: mba.founderFeeBps,   // slot reuse from Share's genesis stashing
      companySplitBps: mba.companySplitBps,
      companyLockingBytecodeHex: mba.founderLockingBytecodeHex,     // Share stashes company LB here
      distributionLockingBytecodeHex: mba.distributionLockingBytecodeHex,
      ticker: mba.ticker,
      name: displayName,
      iconSha256Hex: iconOk ? assetRow?.iconSha256 : undefined,
      iconPath:      iconOk ? iconPath           : undefined,
    } : {
      templateId: TEMPLATE_ARTEMISION_MINT_FT,
      rateSats: mba.rateSats,
      founderFeeBps: mba.founderFeeBps,
      founderLockingBytecodeHex: mba.founderLockingBytecodeHex,
      ticker: mba.ticker,
      name: displayName,
      iconSha256Hex: iconOk ? assetRow?.iconSha256 : undefined,
      iconPath:      iconOk ? iconPath           : undefined,
    };
    const res = await publishViaLocalSigner(localSigner, mba.categoryId, meta);
    logTx({ kind: 'publish', ticker: sym, amount: String(res.feeDea) + ' DEA fee', txid: res.txid });
    body.innerHTML =
      '<div style="color: var(--acid); font-family: var(--font-mono); font-size: 12px; line-height: 1.7">' +
        '✓ Published on chipnet<br>' +
        '<span style="color: var(--muted); font-size: 10px">tx</span> <span style="color: var(--ink-bright); word-break: break-all">' + escHtml(res.txid) + '</span><br>' +
        '<span style="color: var(--muted); font-size: 10px">category</span> <span style="word-break: break-all">' + escHtml(res.targetCategoryId) + '</span><br>' +
        '<span style="color: var(--muted); font-size: 10px">fee</span> ' + String(res.feeDea) + ' atomic DEA' +
      '</div>' +
      '<p class="wc-modal-sub" style="margin-top:12px">Every wallet reading Katalogos will see this listing from the next block. Refresh Potidaea to pick it up in the on-chain read.</p>';
    // Refresh the on-chain read so the banner count updates
    void refreshRegistry();
  } catch (e) {
    const code = (e instanceof PublishError) ? e.code : 'other';
    const msg = e instanceof Error ? e.message : String(e);
    body.innerHTML =
      '<div class="wc-err">✗ ' + escHtml(msg) + '</div>' +
      (code === 'insufficient-dea'
        ? '<p class="wc-modal-sub" style="margin-top:10px">Chipnet DEA is the fee token. To publish, this wallet needs at least the required atomic units of DEA — send some to <code style="font-family:var(--font-mono);font-size:10px;color:var(--acid);word-break:break-all">' + escHtml(localSigner!.cashaddr) + '</code> from a wallet that has DEA, or ask for a chipnet DEA faucet drop.</p>'
        : '');
  }
}

// Wizard file-input auto-upload. Hooks the mockup's #asIconFile so as soon
// as a user picks an image, we fire the Sia upload in parallel with the
// mockup's own preview/hash work. On success we stash the real URL where
// runArtemisionGenesis + runRealPublish look for it, and swap the review-
// sheet "will store at" line from the s3://bns/katalogos/… placeholder
// over to the real https://navigate.st/… URL. On failure we silently keep
// the local preview and the genesis-time upload fallback still fires.
function wireIconAutoUpload(): void {
  const input = document.getElementById('asIconFile') as HTMLInputElement | null;
  if (!input) return;
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (!f) return;
    void handleIconFileUpload(f);
  });
}

async function handleIconFileUpload(f: File): Promise<void> {
  const pathEl = document.getElementById('iconPath');
  if (pathEl) pathEl.textContent = '↑ uploading to Sia…';
  try {
    const bytes = new Uint8Array(await f.arrayBuffer());
    const upload = await uploadIcon(bytes, f.type);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    w.wizIconUploadedUrl = upload.url;
    w.wizIconUploadedSha = upload.sha256Hex;
    if (pathEl) pathEl.textContent = upload.url;
    // Also mirror onto the ASSETS row for the current wizard ticker in
    // case the user has already stepped through to Broadcast — publish
    // will pick these up from either wizIconUploadedUrl or the ASSETS row.
    const sym = (document.getElementById('asSym') as HTMLInputElement | null)?.value?.trim();
    if (sym) {
      const row = window.ASSETS?.find((a) => a.sym === sym) as { iconSha256?: string; iconPath?: string } | undefined;
      if (row) { row.iconSha256 = upload.sha256Hex; row.iconPath = upload.url; }
    }
    console.log('[icon] auto-uploaded', upload.bytes, 'bytes →', upload.url);
  } catch (e) {
    const code = e instanceof IconUploadError ? e.code : 'other';
    if (pathEl) pathEl.textContent = '✗ upload failed (' + code + ') — will retry at Broadcast';
    console.warn('[icon] auto-upload skipped:', e instanceof Error ? e.message : e);
  }
}

// ── Orbital-Design 3-column layout (EtherDelta shape, Potidaea skin) ────
// Left column: assets browser (all discovered + local + seed) with search,
// filter chips (All / Mine / Listed), and click-to-set-`to` behaviour.
// Right column: my holdings (rows with bal > 0) stacked above the tx log.
// The swap card + info panels in the middle are untouched.
//
// All three panels re-render on any of:
//   • boot                (initial paint)
//   • wallet attach/detach (localSigner change)
//   • balance refresh      (paintBalance)
//   • registry discovery   (refreshRegistry)
//   • local tx log update  (logTx)
// The wiring is monkey-patch-style hooks into those existing entry points.

interface TxLogEntry {
  ts: number;
  kind: 'genesis' | 'publish' | 'claim' | 'redeem';
  ticker: string;
  amount?: string;          // display-form ("1000 ASM", "0.001 BCH", etc.)
  txid: string;
  note?: string;
}

const TX_LOG_KEY = 'deviant.potidaea.tx-log.v1';
const TX_LOG_MAX = 60;

function readTxLog(): TxLogEntry[] {
  try {
    const raw = localStorage.getItem(TX_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
}

function logTx(entry: Omit<TxLogEntry, 'ts'>): void {
  const list = readTxLog();
  list.unshift({ ts: Date.now(), ...entry });
  const trimmed = list.slice(0, TX_LOG_MAX);
  try { localStorage.setItem(TX_LOG_KEY, JSON.stringify(trimmed)); } catch { /* noop */ }
  paintOdTxLog();
}

// Asset-list tabs — Favorites (★) / BCH (all) / Olympia (placeholder).
type OdAssetTab = 'favs' | 'bch' | 'olympia';
let odAssetTab: OdAssetTab = 'bch';
// Tx-log tabs — trades / creates / all.
type OdTxTab = 'trades' | 'creates' | 'all';
let odTxTab: OdTxTab = 'trades';

const FAVORITES_KEY = 'deviant.potidaea.favorites.v1';
function readFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch { return new Set(); }
}
function toggleFavorite(sym: string): void {
  const set = readFavorites();
  if (set.has(sym)) set.delete(sym); else set.add(sym);
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify([...set])); } catch { /* noop */ }
}

function wireOdLayout(): void {
  const searchEl = document.getElementById('odAssetsSearch') as HTMLInputElement | null;
  searchEl?.addEventListener('input', () => paintOdAssets());

  // Asset-list tabs
  document.querySelectorAll('#odAssets .od-tab[data-al-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#odAssets .od-tab[data-al-tab]').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      odAssetTab = (btn.getAttribute('data-al-tab') as OdAssetTab) || 'bch';
      paintOdAssets();
    });
  });

  // Tx-log tabs
  document.querySelectorAll('.od-ccbot .od-tab[data-mt-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.od-ccbot .od-tab[data-mt-tab]').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      odTxTab = (btn.getAttribute('data-mt-tab') as OdTxTab) || 'trades';
      paintOdTxLog();
    });
  });

  paintOdAssets();
  paintOdHoldings();
  paintOdTxLog();
}

interface OdAssetRow { sym: string; name?: string; glyph: string; iconUrl?: string; rate: number; bal: number; templateId?: number; isMine: boolean; isListed: boolean; }

function collectOdRows(): OdAssetRow[] {
  const assets = window.ASSETS ?? [];
  const registryCats = new Set((registryEntries ?? []).map((e) => e.categoryId));
  const localCats = new Set(Object.values(loadLocalKnownMbas()).map((m) => m.categoryId));
  return assets.map((a) => {
    const km = KNOWN_MBAS[a.sym];
    const isMine = km ? localCats.has(km.categoryId) : false;
    const isListed = km ? registryCats.has(km.categoryId) : (a.sym === 'BCH' || a.sym === 'ASM' || a.sym === 'ASM2');
    return {
      sym: a.sym,
      name: a.name?.split(' · ')[0],
      glyph: a.glyph,
      iconUrl: a.iconUrl,
      rate: a.rate,
      bal: a.bal,
      templateId: km?.templateId,
      isMine, isListed,
    };
  });
}

function tplBadge(tpl?: number): string {
  if (tpl === 0x01) return '<span class="od-tbadge share">Share</span>';
  if (tpl === 0x02) return '<span class="od-tbadge oracle">Oracle</span>';
  return '';
}

function agInner(row: { iconUrl?: string; glyph: string }): string {
  if (row.iconUrl) return '<img src="' + row.iconUrl + '" alt="">';
  return escHtml(row.glyph);
}

function paintOdAssets(): void {
  const listEl = document.getElementById('odAssetsList');
  if (!listEl) return;
  const search = ((document.getElementById('odAssetsSearch') as HTMLInputElement | null)?.value ?? '').trim().toLowerCase();
  const favs = readFavorites();
  let rows = collectOdRows();

  // Tab filters
  if (odAssetTab === 'favs') {
    rows = rows.filter((r) => favs.has(r.sym));
    if (!rows.length && !search) {
      listEl.innerHTML = '<div class="od-empty">No favorites yet.<br>Tap ★ on any row to add.</div>';
      return;
    }
  } else if (odAssetTab === 'olympia') {
    listEl.innerHTML = '<div class="od-empty">Olympia listings coming soon — a promoted-asset directory sits here.</div>';
    return;
  }
  // BCH tab: all rows pass through

  if (search) rows = rows.filter((r) => r.sym.toLowerCase().includes(search) || r.name?.toLowerCase().includes(search));
  if (!rows.length) { listEl.innerHTML = '<div class="od-empty">No matches</div>'; return; }
  const activeTo = window.to;

  listEl.innerHTML = rows.map((r) => {
    const balStr = r.bal > 0 ? (r.sym === 'BCH' ? r.bal.toFixed(4) : Math.round(r.bal).toLocaleString('en-US')) : '—';
    const balClass = r.bal > 0 ? '' : ' class="zero"';
    const starOn = favs.has(r.sym);
    return '<div class="od-arow ' + (r.sym === activeTo ? 'on' : '') + '" data-sym="' + escAttr(r.sym) + '">' +
      '<span class="od-star ' + (starOn ? 'on' : '') + '" data-fav="' + escAttr(r.sym) + '" title="' + (starOn ? 'Un-favorite' : 'Add to favorites') + '">' + (starOn ? '★' : '☆') + '</span>' +
      '<span class="od-ag">' + agInner(r) + '</span>' +
      '<div class="od-meta"><div class="sym">' + escHtml(r.sym) + tplBadge(r.templateId) + '</div>' +
      '<div class="sub">' + (r.name ? escHtml(r.name) : (r.sym === 'BCH' ? 'Bitcoin Cash' : 'MBA')) + '</div></div>' +
      '<div class="od-bal"><span' + balClass + '>' + balStr + '</span></div>' +
    '</div>';
  }).join('');

  // Star toggles — stopPropagation so a star tap doesn't also fire the row click.
  listEl.querySelectorAll('.od-star[data-fav]').forEach((star) => {
    star.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const sym = (star as HTMLElement).getAttribute('data-fav'); if (!sym) return;
      toggleFavorite(sym);
      paintOdAssets();
    });
  });
  // Row → set as `to`
  listEl.querySelectorAll('.od-arow').forEach((row) => {
    row.addEventListener('click', () => {
      const sym = row.getAttribute('data-sym'); if (!sym) return;
      if (window.from === sym) window.from = 'BCH';
      window.to = sym;
      try { window.recalc?.(); } catch { /* noop */ }
      paintOdAssets();
      updateAmountPlaceholder();
      // Also update the Activity panel title to the newly-picked ticker
      const t = document.getElementById('odActTitle');
      if (t) t.textContent = 'Activity · ' + sym;
    });
  });
}

function paintOdHoldings(): void {
  const holdings = document.getElementById('odHoldingsList');
  const totalEl = document.getElementById('odTotalUsd');
  if (!holdings) return;
  const rows = collectOdRows().filter((r) => r.bal > 0);
  if (!rows.length) { holdings.innerHTML = '<div class="od-empty">' + (localSigner ? 'No holdings yet' : 'No wallet attached') + '</div>'; if (totalEl) totalEl.textContent = '≈ $0'; return; }
  let totalBch = 0;
  const parts = rows.map((r) => {
    const valBch = r.sym === 'BCH' ? r.bal : (r.bal * r.rate / 1e8);
    totalBch += valBch;
    const displayBal = r.sym === 'BCH' ? r.bal.toFixed(4) : Math.round(r.bal).toLocaleString('en-US');
    const usd = (valBch * bchUsd()).toLocaleString('en-US', { maximumFractionDigits: 2 });
    return '<div class="od-hrow">' +
      '<span class="od-ag">' + agInner(r) + '</span>' +
      '<span class="sym">' + escHtml(r.sym) + '</span>' +
      '<span class="amt">' + displayBal + '<span class="usd">≈ $' + usd + '</span></span>' +
    '</div>';
  });
  holdings.innerHTML = parts.join('');
  if (totalEl) totalEl.textContent = '≈ $' + (totalBch * bchUsd()).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function relTime(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return secs + 's';
  if (secs < 3600) return Math.floor(secs / 60) + 'm';
  if (secs < 86400) return Math.floor(secs / 3600) + 'h';
  return Math.floor(secs / 86400) + 'd';
}

function paintOdTxLog(): void {
  const el = document.getElementById('odTxLog'); if (!el) return;
  let log = readTxLog();
  // Tab filter:
  //   trades  → claim + redeem (actual swaps)
  //   creates → genesis + publish (asset lifecycle)
  //   all     → everything
  if (odTxTab === 'trades')  log = log.filter((e) => e.kind === 'claim' || e.kind === 'redeem');
  else if (odTxTab === 'creates') log = log.filter((e) => e.kind === 'genesis' || e.kind === 'publish');
  if (!log.length) {
    el.innerHTML = '<div class="od-empty">No ' + (odTxTab === 'all' ? 'transactions' : odTxTab) + ' yet</div>';
    return;
  }
  const ICON: Record<string, string> = { genesis: '⊕', publish: '⊚', claim: '↗', redeem: '↩' };
  const LABEL: Record<string, string> = { genesis: 'Genesis', publish: 'Publish', claim: 'Swap', redeem: 'Redeem' };
  el.innerHTML = log.map((e) => (
    '<div class="od-tx" data-txid="' + escAttr(e.txid) + '" title="' + escAttr(e.txid) + '">' +
      '<span class="od-tico ' + e.kind + '">' + ICON[e.kind] + '</span>' +
      '<div class="od-tmeta"><div class="head">' + LABEL[e.kind] + ' · ' + escHtml(e.ticker) + '</div>' +
      '<div class="sub">' + (e.amount ? escHtml(e.amount) + ' · ' : '') + e.txid.slice(0, 10) + '…</div></div>' +
      '<div class="od-tage">' + relTime(e.ts) + '</div>' +
    '</div>'
  )).join('');
  el.querySelectorAll('.od-tx').forEach((row) => {
    row.addEventListener('click', () => {
      const txid = row.getAttribute('data-txid'); if (!txid) return;
      // Chipnet block explorer — same one the wallet uses.
      window.open('https://chipnet.imaginary.cash/tx/' + txid, '_blank', 'noopener,noreferrer');
    });
  });
}

function paintOdAll(): void { paintOdAssets(); paintOdHoldings(); paintOdTxLog(); }

// Decode a `data:image/…;base64,…` URL into raw bytes + content-type. The
// mockup's file picker stores uploaded icons in this form on window.
function dataUrlToBytes(dataUrl: string): { bytes: Uint8Array; contentType: string } {
  const m = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!m) throw new Error('unexpected data-url shape');
  const contentType = m[1];
  const bin = atob(m[2]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { bytes, contentType };
}

function attachWellKnownIcons(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  const bch = assets.find((a) => a.sym === 'BCH');
  if (bch && !bch.iconUrl) bch.iconUrl = BCH_ICON_DATA_URI;
  // Force any already-rendered surface to redraw with the new icon.
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
}

function zeroAllBalances(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  for (const a of assets) a.bal = 0;
  const bchEl = document.getElementById('chipBch');
  if (bchEl) bchEl.textContent = '0.0000 BCH';
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { paintOdAll(); } catch { /* noop */ }
}

// ── Default amount placeholder ─────────────────────────────────────────────
// Clears the hard-coded "1000" and installs a live hint that reads
// "≈ $20 in <FROM>" — because 1000 BCH and 1000 ASM are wildly different
// value orders, a single hard default makes no sense across assets. The
// hint is oracle-decorative (bchUsd from CoinGecko / CryptoCompare with a
// fallback constant), not a settlement price — the user still types the
// exact amount they want.
//
// Wire: overrides #amtIn's initial value + placeholder, then wraps
// window.recalc so any FROM change (asset picker, flip button, max click,
// direct input) refreshes the placeholder to the new denomination.

const DEFAULT_USD_TARGET = 20;

function currentFromAsset(): { sym: string; rate: number; bal: number } | null {
  const from = window.from;
  if (!from) return null;
  const a = window.ASSETS?.find((x) => x.sym === from);
  if (!a) return null;
  return { sym: a.sym, rate: a.rate, bal: a.bal };
}

function computeHintForFrom(fromSym: string, fromRate: number): { units: number; label: string } {
  // rate = sats per unit. 1 BCH → 1e8 sats. $20 in USD = 20/bchUsd BCH = 20*1e8/bchUsd sats.
  const targetSats = (DEFAULT_USD_TARGET * 1e8) / bchUsd();
  const units = targetSats / fromRate;
  // Precision heuristic:
  //   BCH        → 6 dp (small fractions matter)
  //   fine MBAs  → 2 dp for large numbers, no dp when > 1000
  //   default    → 4 dp
  let str: string;
  if (fromSym === 'BCH') str = units.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  else if (units >= 1000) str = Math.round(units).toLocaleString('en-US');
  else if (units >= 1) str = units.toFixed(2);
  else str = units.toPrecision(2);
  return { units, label: `e.g. ${str} ${fromSym} (≈ $${DEFAULT_USD_TARGET})` };
}

function updateAmountPlaceholder(): void {
  const inp = document.getElementById('amtIn') as HTMLInputElement | null;
  if (!inp) return;
  const a = currentFromAsset();
  if (!a) { inp.placeholder = 'amount'; return; }
  const { label } = computeHintForFrom(a.sym, a.rate);
  inp.placeholder = label;
}

// Patches the Balances page's USD total. The mockup's own renderBalances
// derives bchUsd from a USDm asset that doesn't exist in ASSETS, so the
// total collapses to "≈ 0 USD". Overwrite the label with a real number
// from our oracle every time renderBalances runs.
function installBalancesUsdPatch(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const original = w.renderBalances;
  if (typeof original !== 'function' || w.__potidaeaBalancesWrapped) return;
  w.renderBalances = function wrapped() {
    const r = original.apply(this, arguments as unknown as unknown[]);
    try {
      const el = document.getElementById('balTotalUsd');
      const bchEl = document.getElementById('balTotalBch');
      if (el && bchEl) {
        const bch = parseFloat((bchEl.textContent || '').replace(/[^0-9.]/g, '')) || 0;
        const usd = bch * bchUsd();
        el.textContent = '≈ $' + usd.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' · via CoinGecko';
      }
    } catch { /* noop */ }
    return r;
  };
  w.__potidaeaBalancesWrapped = true;
}

function installAmountPlaceholder(): void {
  const inp = document.getElementById('amtIn') as HTMLInputElement | null;
  if (!inp) return;
  // Only clear the hard-coded "1000". Don't touch a value the user has
  // already typed (in the unlikely case boot runs after they've started).
  if (inp.value === '1000') inp.value = '';
  updateAmountPlaceholder();

  // Wrap the mockup's recalc so we run after every state change (asset
  // picker → new from, flip button → swapped from/to, max click →
  // typed value, direct input → user typed). recalc is defined on
  // window via the mockup's non-strict-mode inline script.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const originalRecalc = w.recalc;
  if (typeof originalRecalc === 'function' && !w.__potidaeaRecalcWrapped) {
    w.recalc = function wrapped() {
      const r = originalRecalc.apply(this, arguments as unknown as unknown[]);
      try { updateAmountPlaceholder(); } catch { /* noop */ }
      return r;
    };
    w.__potidaeaRecalcWrapped = true;
  }
}
// <script type="module"> is deferred, so by the time this file runs the DOM is
// usually already parsed. Handle both cases: run boot immediately if the body
// is present, otherwise wait for DOMContentLoaded.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

window.potidaeaWc = {
  connect: () => { wc.connect(); },
  disconnect: () => { wc.disconnect(); },
  refreshBalance,
  get cashaddr() { return wc.snapshot.cashaddr; },
};

// Console helpers for the dev-signer mode (localhost only — see signer-store.ts).
// Use: window.potidaeaDev.pinWif('cRA1KX…'); location.reload();
(window as unknown as { potidaeaDev: unknown }).potidaeaDev = {
  pinWif: pinDevWif,
  clear: clearDevWif,
  get signer() { return localSigner; },
};

// ── Wallet menu — repaint the mockup's own #walletMenu ─────────────────────
// The mockup already has a coordinated pair of header dropdowns (#netMenu +
// #walletMenu) with mutual-exclusion (opening one closes the other) and
// outside-click dismissal. We repaint #walletMenu's contents rather than
// float our own menu — that way the mockup's toggle / closeAll / outside-
// click logic works with our items unchanged.

function paintWalletMenu(): void {
  const menu = document.getElementById('walletMenu');
  if (!menu) return;

  const items: string[] = [];
  const stored = listUserWallets();
  const wcAddr = (wc.snapshot.phase === 'paired' && wc.snapshot.cashaddr) ? wc.snapshot.cashaddr : null;

  // Section 1 — every stored user wallet, active first (marked with a
  // filled dot). Non-active rows are switchable in one click; each row has
  // an ✕ that removes just that wallet from storage without touching the
  // rest. When the active row is removed, the next stored wallet takes
  // over automatically (or the store empties).
  if (stored.length || wcAddr) {
    items.push('<div class="hm-head">Your wallets</div>');
    for (const w of stored) {
      const short = w.cashaddr.slice(0, 14) + '…' + w.cashaddr.slice(-6);
      items.push(
        '<div class="hm-item ' + (w.isActive ? 'on' : '') + '" data-act="' + (w.isActive ? 'noop' : 'switch') + '" data-addr="' + escAttr(w.cashaddr) + '" title="' + escAttr(w.cashaddr) + '">' +
          '<span class="hm-dot ' + (w.isActive ? 'on' : '') + '"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">' + escHtml(w.label) + '</span>' +
          '<button class="hm-x" data-act="rename" data-addr="' + escAttr(w.cashaddr) + '" data-label="' + escAttr(w.label) + '" title="Rename this wallet">✎</button>' +
          '<button class="hm-x" data-act="copy"   data-addr="' + escAttr(w.cashaddr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="remove" data-addr="' + escAttr(w.cashaddr) + '" title="Remove this wallet from storage">✕</button>' +
        '</div>',
      );
    }
    if (wcAddr) {
      const short = wcAddr.slice(0, 14) + '…' + wcAddr.slice(-6);
      const isActiveWc = !localSigner;   // WC wins only when no local signer is active
      items.push(
        '<div class="hm-item ' + (isActiveWc ? 'on' : '') + '" title="' + escAttr(wcAddr) + '">' +
          '<span class="hm-dot ' + (isActiveWc ? 'on' : '') + '"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">WC</span>' +
          '<button class="hm-x" data-act="copy" data-addr="' + escAttr(wcAddr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="disconnect" title="Unpair the WalletConnect session">✕</button>' +
        '</div>',
      );
    }
    // Dev-mode signer, if active but not tracked in the user store
    if (localSigner?.mode === 'dev') {
      const addr = localSigner.cashaddr;
      const short = addr.slice(0, 14) + '…' + addr.slice(-6);
      items.push(
        '<div class="hm-item on" title="' + escAttr(addr) + ' (dev signer · localhost only)">' +
          '<span class="hm-dot on"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">dev</span>' +
          '<button class="hm-x" data-act="copy" data-addr="' + escAttr(addr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="detach-dev" title="Clear dev signer">✕</button>' +
        '</div>',
      );
    }
    items.push('<div class="hm-sep"></div>');
  }

  // Section 2 — three ways to attach a new wallet. Order per request.
  items.push(
    '<div class="hm-head">Attach another</div>',
    '<div class="hm-item" data-act="create">＋ Create a wallet<span class="hm-sub">generate a key in this browser</span></div>',
    '<div class="hm-item" data-act="import">↧ Import a wallet<span class="hm-sub">paste a WIF into this browser</span></div>',
    '<div class="hm-item" data-act="wizard">⌘ Wizard Connect<span class="hm-sub">pair a BCH wallet</span></div>',
  );

  menu.innerHTML = items.join('');
  menu.classList.add('wide');

  // Wire clicks. Any click inside the menu must stopPropagation so the
  // mockup's document-level closeAll doesn't double-fire. The X buttons
  // must also stopPropagation before their row's own handler runs (they
  // live INSIDE the row div, so a click on ✕ would bubble to the row and
  // trigger a switch too — the inner handler catches it first).
  menu.querySelectorAll('button.hm-x').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const el = btn as HTMLElement;
      const act = el.getAttribute('data-act');
      const addr = el.getAttribute('data-addr') ?? '';
      if (act === 'remove') removeStoredWallet(addr);
      else if (act === 'disconnect') wc.disconnect();
      else if (act === 'detach-dev') detachLocalSigner();
      else if (act === 'copy') copyAddressToClipboard(addr, el);
      else if (act === 'rename') runRenameWallet(addr, el.getAttribute('data-label') ?? '');
    });
  });
  menu.querySelectorAll('.hm-item[data-act]').forEach((row) => {
    row.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const el = row as HTMLElement;
      const act = el.getAttribute('data-act');
      if (act === 'switch') {
        const addr = el.getAttribute('data-addr');
        if (addr) doSwitchToWallet(addr);
        closeAllHeaderMenus();
        return;
      }
      if (act === 'noop') return;   // clicking the already-active row does nothing
      closeAllHeaderMenus();
      if (act === 'create') openCreateModal();
      else if (act === 'import') openImportModal();
      else if (act === 'wizard') wc.connect();
    });
  });
}

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c] as string));
}
function escAttr(s: string): string { return escHtml(s); }

function doSwitchToWallet(cashaddr: string): void {
  const s = switchUserWallet(cashaddr);
  if (!s) return;
  localSigner = s;
  renderChip();
  paintWalletMenu();
  void refreshBalance();
}

function removeStoredWallet(cashaddr: string): void {
  // Nice to warn before deleting the ACTIVE one (it changes what the app
  // is talking to), but a non-active wallet is a low-stakes remove; still
  // confirm because a WIF loss is unrecoverable.
  const isActive = !!localSigner && localSigner.cashaddr === cashaddr;
  const msg = isActive
    ? 'Remove this active wallet? Its WIF stored in this browser will be cleared. If you have not backed it up, funds at this address are unrecoverable.'
    : 'Remove this wallet from storage? Its WIF will be cleared. If you have not backed it up, funds at this address are unrecoverable.';
  if (!confirm(msg)) return;
  const next = removeUserWallet(cashaddr);
  if (isActive) {
    localSigner = next;
    renderChip();
    if (next) void refreshBalance();
    else zeroAllBalances();
  }
  paintWalletMenu();
}

function copyAddressToClipboard(cashaddr: string, btn: HTMLElement): void {
  const write = (): Promise<void> =>
    (navigator.clipboard && navigator.clipboard.writeText)
      ? navigator.clipboard.writeText(cashaddr)
      // Fallback for older browsers / non-HTTPS contexts — build a hidden
      // textarea, select, execCommand.
      : new Promise<void>((resolve, reject) => {
          try {
            const ta = document.createElement('textarea');
            ta.value = cashaddr;
            ta.style.cssText = 'position:fixed;left:-9999px;top:0;';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
            resolve();
          } catch (e) { reject(e); }
        });
  void write().then(() => {
    // Visible confirmation — swap the icon text briefly, restore after 1.2s.
    const original = btn.textContent;
    btn.textContent = '✓';
    setTimeout(() => { btn.textContent = original; }, 1200);
  }).catch((e) => {
    console.warn('[wallet] copy failed:', e instanceof Error ? e.message : e);
  });
}

function runRenameWallet(cashaddr: string, currentLabel: string): void {
  const next = window.prompt('New label for this wallet:', currentLabel);
  if (next == null) return;                 // user cancelled
  const cleaned = next.trim();
  if (!cleaned) return;                     // empty — keep existing
  if (cleaned === currentLabel) return;     // no change
  const ok = renameUserWallet(cashaddr, cleaned);
  if (!ok) { console.warn('[wallet] rename failed for', cashaddr); return; }
  paintWalletMenu();
}

function closeAllHeaderMenus(): void {
  document.getElementById('walletMenu')?.classList.remove('open');
  document.getElementById('netMenu')?.classList.remove('open');
}

function openImportModal(): void {
  const el = document.createElement('div');
  el.className = 'wc-scrim'; el.id = 'wc-import';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Import WIF</div>' +
      '<p class="wc-modal-sub">Paste a WIF-encoded private key. It stays in this browser (localStorage) — nothing is uploaded.</p>' +
      '<div class="wc-warn">⚠ The page holds the key while attached. Use a key you\'re willing to expose to the browser — not one guarding significant funds.</div>' +
      '<label class="wc-label">WIF <input id="wc-imp-wif" type="password" autocomplete="off" spellcheck="false" placeholder="c… or K/L…" /></label>' +
      '<div id="wc-imp-err" class="wc-err" hidden></div>' +
      '<div class="wc-actions">' +
        '<button class="wc-primary" id="wc-imp-go">Attach</button>' +
        '<button class="wc-cancel">Cancel</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(el);
  const close = (): void => el.remove();
  el.addEventListener('click', (ev) => { if (ev.target === el) close(); });
  el.querySelector('.wc-cancel')?.addEventListener('click', close);
  el.querySelector('#wc-imp-go')?.addEventListener('click', () => {
    const input = el.querySelector('#wc-imp-wif') as HTMLInputElement;
    const err = el.querySelector('#wc-imp-err') as HTMLElement;
    const wif = input.value.trim();
    if (!wif) { err.textContent = 'WIF required'; err.hidden = false; return; }
    const s = importUserWif(wif);
    if (!s) { err.textContent = 'Invalid WIF — check for typos'; err.hidden = false; return; }
    if (!s.cashaddr.startsWith('bchtest:')) {
      clearUserWallet();
      err.textContent = 'WIF network does not match the network selected above (address ' + s.cashaddr.slice(0, 10) + '…).';
      err.hidden = false;
      return;
    }
    localSigner = s;
    close();
    renderChip();
    paintWalletMenu();
    void refreshBalance();
  });
  (el.querySelector('#wc-imp-wif') as HTMLInputElement | null)?.focus();
}

function openCreateModal(): void {
  const res = createUserWallet();
  const wif = res?.wif ?? '';
  const addr = res?.signer.cashaddr ?? '(create failed)';
  const el = document.createElement('div');
  el.className = 'wc-scrim'; el.id = 'wc-create';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">New wallet</div>' +
      '<p class="wc-modal-sub">A fresh key was generated for this browser. It is stored in localStorage. There is no seed phrase and no recovery — clear browser data and the key is gone.</p>' +
      '<div class="wc-warn">⚠ The page holds the key while attached. Back up the WIF now if you want to keep this address.</div>' +
      '<label class="wc-label">Address<code class="wc-code" id="wc-cre-addr">' + escapeHtml(addr) + '</code></label>' +
      '<label class="wc-label">WIF (back this up now if you want it) ' +
        '<span class="wc-reveal-row">' +
          '<code class="wc-code wc-mask" id="wc-cre-wif" data-wif="' + escapeAttr(wif) + '">••••••••••••••••••••••••••••••••••••••••••••••••••</code>' +
          '<button class="wc-mini" id="wc-cre-show">Show</button>' +
          '<button class="wc-mini" id="wc-cre-copy">Copy</button>' +
        '</span>' +
      '</label>' +
      '<p class="wc-modal-sub">Fund this address to run swaps.</p>' +
      '<div class="wc-actions">' +
        '<button class="wc-primary" id="wc-cre-ok">Done</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(el);
  const close = (): void => el.remove();
  el.addEventListener('click', (ev) => { if (ev.target === el) close(); });
  el.querySelector('#wc-cre-ok')?.addEventListener('click', () => {
    close();
    if (res) { localSigner = res.signer; renderChip(); paintWalletMenu(); void refreshBalance(); }
  });
  el.querySelector('#wc-cre-show')?.addEventListener('click', () => {
    const codeEl = el.querySelector('#wc-cre-wif') as HTMLElement;
    codeEl.textContent = wif;
    codeEl.classList.remove('wc-mask');
  });
  el.querySelector('#wc-cre-copy')?.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(wif); } catch { /* clipboard blocked */ }
    const btn = el.querySelector('#wc-cre-copy') as HTMLButtonElement;
    btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c] as string));
}
function escapeAttr(s: string): string { return escapeHtml(s); }

// Expose the reveal helper so a paranoid user can peek at their WIF later
// via the console (or from a "Show WIF" button we might add to Settings).
(window as unknown as { potidaeaWallet: unknown }).potidaeaWallet = {
  revealWif: () => readUserWif(),
  clear: () => { clearUserWallet(); localSigner = null; renderChip(); },
  get signer() { return localSigner; },
};

// Console API for Share MBA genesis — the wizard is Artemision-only for
// now, so this is the (developer-visible) way to mint + publish a Share
// asset. Once the wizard grows a template picker this becomes the fast
// path for testing.
async function genesisShareViaConsole(input: {
  ticker: string;
  rateSats: number | bigint;
  feeBps: number | bigint;
  companySplitBps: number | bigint;
  companyLockingBytecodeHex?: string;      // defaults to signer's own P2PKH
  distributionLockingBytecodeHex?: string; // defaults to signer's own P2PKH
  name?: string; glyph?: string;
}): Promise<ShareGenesisResult> {
  if (!localSigner) throw new Error('attach a wallet first');
  // Default both LB fields to the signer's own P2PKH so a solo dev can smoke
  // the full loop without staging separate keys for company / distribution.
  const ownLB = pkhToP2PKHLBHex(localSigner.publicKeyHash);
  const params: ShareGenesisParams = {
    ticker: input.ticker,
    rateSats: BigInt(input.rateSats),
    feeBps: BigInt(input.feeBps),
    companySplitBps: BigInt(input.companySplitBps),
    companyLockingBytecodeHex: input.companyLockingBytecodeHex ?? ownLB,
    distributionLockingBytecodeHex: input.distributionLockingBytecodeHex ?? ownLB,
    name: input.name,
    glyph: input.glyph,
  };
  const res = await genesisDeviantShareMBA(localSigner, params);
  // Rehydrate ASSETS + KNOWN_MBAS so the new Share MBA is swappable
  // immediately in this session.
  hydrateLocalMbas();
  filterAssetsToSwappable();
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  return res;
}

function pkhToP2PKHLBHex(pkh: Uint8Array): string {
  const lb = new Uint8Array(25);
  lb[0] = 0x76; lb[1] = 0xa9; lb[2] = 0x14; lb.set(pkh, 3); lb[23] = 0x88; lb[24] = 0xac;
  let s = ''; for (const b of lb) s += b.toString(16).padStart(2, '0'); return s;
}

/** Decode a cashaddr (bchtest: or its token-aware sibling) → 25-byte P2PKH
 *  locking bytecode hex. Returns null on any decode failure so the caller
 *  can surface a friendly error to the wizard user. */
function cashaddrToP2PKHLBHex(cashaddr: string): string | null {
  try {
    const dec = decodeCashAddress(cashaddr.trim());
    if (typeof dec === 'string') return null;
    // decodeCashAddress returns { prefix, type, payload } — payload is the
    // 20-byte hash160 regardless of whether the address is token-aware.
    if (!dec.payload || dec.payload.length !== 20) return null;
    return pkhToP2PKHLBHex(dec.payload);
  } catch { return null; }
}

(window as unknown as { potidaeaShare: unknown }).potidaeaShare = {
  genesis: genesisShareViaConsole,
  // Convenience: publish and end-to-end publish helpers reuse the existing
  // openPublishOverlay path; a Share ticker in KNOWN_MBAS gets the right
  // DVMB blob via runRealPublish (which reads templateId off the row).
};
